import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { Dateien } from './kontakte/Dateien';
import { mitGeloeschtText } from './kontakte/KontaktDaten';
import { Personen } from './kontakte/Personen';
import { SchlagwortChips } from './kontakte/Schlagwoerter';
import { ART_NAME, kundeUrl, landName, rechnungen, tage, type KundeAntwort, type KundeRechnung } from './KundeDaten';
import { istDrittland } from './laender-welt';
import { waehrungName } from './waehrung';
import { PdfDialog } from './PdfDialog';
import { darf } from './tarifHinweis';
import { MAHN_TITLES, type Overview } from './types';
import { stundenText } from './zeiten/dauer';
import { eintraegeText } from './zeiten/ZeitDaten';
import './kunden.css';

const pdfUrl = (id: string) => `/api/rechnungstool/rechnung/${id}/pdf`;
const ART_TITEL = { normal: 'Rechnung', abschlag: 'Abschlagsrechnung', schluss: 'Schlussrechnung' } as const;
// Zusatz in den Kennzahlen, wenn Rechnungen in anderer Währung darin stecken (vom Server in Euro umgerechnet)
const UMGERECHNET = ' · umgerechnet zum Kurs der Belege';
const docName = (r: KundeRechnung) => (r.kind === 'storno' ? 'Stornorechnung' : r.kind === 'korrektur' ? 'Rechnungskorrektur' : ART_TITEL[r.art]);

// Ein Kunde (/rechnung/kunden/:id): Angaben, Umsatz, Offenes, Zahlungsdauer, alle Rechnungen an ihn; bearbeiten,
// Rechnung schreiben, entfernen (nur ohne Rechnungen und ohne offene Zeiten). Billtoro: über den Rechnungen die Zeiten zu
// diesem Kunden (nur wenn es in der Sicht des Logins welche gibt). Unter den Angaben die Ansprechpartner und (nur
// Billtoro) die Dateien. Firma oben gewechselt: zur Liste der neuen Firma.
export function KundeDetail({ data, onChanged }: { data: Overview; onChanged(): Promise<void> }) {
  const { id = '' } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [res, setRes] = useState<KundeAntwort | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<KundeRechnung | null>(null);
  const [weg, setWeg] = useState(false);
  // Billtoro: Rechte des Logins (nur Anzeige, der Server prüft)
  const pflegen = darf(data.tarif, 'kunden');
  const rechnen = darf(data.tarif, 'rechnungen');
  const [busy, setBusy] = useState(false);
  const firma = useRef(data.firm.id);
  // Antwort zu einem anderen Kunden (inzwischen weiter geklickt) verwerfen
  const aktuell = useRef(id);
  aktuell.current = id;

  const laden = useCallback(async () => {
    try {
      const r = await api.get<KundeAntwort>(kundeUrl(id));
      if (aktuell.current === id) setRes(r);
    } catch (err) {
      if (aktuell.current === id) setError(errorText(err));
    }
  }, [id]);

  useEffect(() => {
    setRes(null);
    setError(null);
    void laden();
  }, [laden]);

  useEffect(() => {
    if (firma.current !== data.firm.id) navigate('/rechnung/kunden', { replace: true });
  }, [data.firm.id, navigate]);

  if (error) {
    return (
      <div className="notice bad">
        {error} <Link to="/rechnung/kunden">Zur Kundenliste</Link>
      </div>
    );
  }
  if (!res) return <div className="spinner" aria-label="Lädt" />;
  const k = res.kunde;
  const ziel = k.paymentDays ?? data.absender.paymentDays;
  const mitDazu = mitGeloeschtText(res.personen, res.dateien);
  // Ansprechpartner oder Dateien geändert: Kunde neu (die Liste lädt beim Zurückkommen selbst)
  const geaendert = laden;

  async function entfernen() {
    setBusy(true);
    try {
      await api.del(kundeUrl(k.id));
      await onChanged();
      toast(`Kunde „${k.name}“ entfernt`);
      navigate('/rechnung/kunden', { replace: true });
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
      setWeg(false);
    }
  }

  return (
    <>
      <Link to="/rechnung/kunden" className="kd-back">
        ← Alle Kunden
      </Link>
      <div className="rt-head kd-head">
        <div className="kd-title">
          <h1>{k.name}</h1>
          <p className="muted">
            Kundennummer <span className="num">{k.nummer}</span> · {ART_NAME[k.art]}
            {k.country !== 'DE' ? ` · ${landName(k.country)}` : ''}
            {k.waehrung !== 'EUR' && (
              <>
                {' '}
                <span className="badge b-none" title="Währung für neue Belege">
                  {k.waehrung}
                </span>
              </>
            )}
            {k.mahnstopp && (
              <>
                {' '}
                <span className="badge b-none" title="Nicht automatisch mahnen">
                  Mahnstopp
                </span>
              </>
            )}
          </p>
          <SchlagwortChips woerter={k.schlagwoerter} zeige={10} />
        </div>
        {(pflegen || rechnen) && (
          <div className="rt-head-act">
            {pflegen && (
              <Link to="bearbeiten" className="btn">
                Bearbeiten
              </Link>
            )}
            {rechnen && (
              <Link to={`/rechnung/neu?kunde=${k.id}`} className="btn primary">
                Rechnung schreiben
              </Link>
            )}
          </div>
        )}
      </div>

      {k.warnung === 'ueberfaellig' ? (
        <div className="notice bad kd-warn">
          <b>Überfällig: {eurC(k.ueberfaellig)}</b> aus {rechnungen(k.ueberfaelligAnzahl)}. Erinnern oder mahnen Sie in der{' '}
          <Link to="/rechnung/rechnungen">Rechnungsliste</Link>.
        </div>
      ) : (
        k.warnung === 'langsam' && (
          <div className="notice warn kd-warn">
            <b>Zahlt langsam:</b> im Schnitt nach {tage(k.zahlungsdauer ?? 0)}, das Zahlungsziel ist {tage(ziel)}.
          </div>
        )
      )}

      <div className="kd-kpis">
        <div className="rt-kpi">
          <span className="rt-kpi-l">Umsatz {res.jahr}</span>
          <span className="rt-kpi-v num">{eurC(k.umsatzJahr)}</span>
          <span className="rt-kpi-s">
            netto · {res.jahr - 1}: {eurC(k.umsatzVorjahr)}
            {k.fremd ? UMGERECHNET : ''}
          </span>
        </div>
        <div className="rt-kpi">
          <span className="rt-kpi-l">Umsatz gesamt</span>
          <span className="rt-kpi-v num">{eurC(k.umsatzGesamt)}</span>
          <span className="rt-kpi-s">
            netto · {rechnungen(k.rechnungen)}
            {k.fremd ? UMGERECHNET : ''}
          </span>
        </div>
        <div className={`rt-kpi${k.ueberfaellig > 0 ? ' bad' : ''}`}>
          <span className="rt-kpi-l">Offen</span>
          <span className="rt-kpi-v num">{eurC(k.offen)}</span>
          <span className="rt-kpi-s">
            {k.ueberfaellig > 0 ? `davon ${eurC(k.ueberfaellig)} überfällig` : k.offenAnzahl ? rechnungen(k.offenAnzahl) : 'nichts offen'}
            {k.fremd && k.offenAnzahl ? UMGERECHNET : ''}
          </span>
        </div>
        <div className={`rt-kpi${k.warnung === 'langsam' ? ' kd-langsam' : ''}`}>
          <span className="rt-kpi-l">Zahlt im Schnitt nach</span>
          <span className="rt-kpi-v">{k.zahlungsdauer == null ? '–' : tage(k.zahlungsdauer)}</span>
          <span className="rt-kpi-s">Zahlungsziel {tage(ziel)}</span>
        </div>
      </div>

      <div className="kd-cols">
        <div className="kt-links">
          <section className="rt-card kd-angaben" aria-labelledby="kd-angaben-h">
            <h2 id="kd-angaben-h">Angaben</h2>
            <dl>
              <Zeile titel="Anschrift">
                <span className="kd-adr">
                  {[k.name, k.extra, k.street, `${k.zip} ${k.city}`.trim(), k.country !== 'DE' ? landName(k.country) : null]
                    .filter(Boolean)
                    .join('\n')}
                </span>
              </Zeile>
              {k.email && (
                <Zeile titel="E-Mail">
                  <a href={`mailto:${k.email}`}>{k.email}</a>
                </Zeile>
              )}
              {k.emailCc && <Zeile titel="Kopie an">{k.emailCc}</Zeile>}
              {k.phone && (
                <Zeile titel="Telefon">
                  <a href={`tel:${k.phone.replace(/[^\d+]/g, '')}`}>{k.phone}</a>
                </Zeile>
              )}
              {k.vatId && (
                <Zeile titel={istDrittland(k.country) ? 'Steuernummer' : 'USt-IdNr.'}>
                  <span className="num">{k.vatId}</span>
                </Zeile>
              )}
              {k.waehrung !== 'EUR' && <Zeile titel="Währung">{waehrungName(k.waehrung)} für neue Belege</Zeile>}
              <Zeile titel="Zahlungsziel">
                {tage(ziel)}
                {k.paymentDays == null && <span className="muted"> (wie in Ihren Angaben)</span>}
              </Zeile>
              {(k.xrechnung || k.buyerRef) && (
                <Zeile titel={k.xrechnung ? 'XRechnung' : 'Leitweg-ID'}>
                  {k.buyerRef ? <span className="num">{k.buyerRef}</span> : 'ja'}
                  {k.xrechnung && k.buyerRef && <span className="muted"> (Leitweg-ID)</span>}
                </Zeile>
              )}
              {k.notiz && (
                <Zeile titel="Notiz">
                  <span className="kd-notiz">{k.notiz}</span>
                </Zeile>
              )}
            </dl>
          </section>
          <Personen art="kunde" kontaktId={k.id} personen={res.personen} pflegen={pflegen} onChanged={geaendert} />
          {res.dateien && (
            <Dateien
              art="kunde"
              kontaktId={k.id}
              dateien={res.dateien}
              maxMb={res.dateienMb ?? 25}
              tarif={data.tarif}
              pflegen={pflegen}
              onChanged={geaendert}
            />
          )}
        </div>

        <section className="kd-rechnungen" aria-labelledby="kd-rechnungen-h">
          {res.zeiten && (
            <div className="rt-card kd-zeiten" role="group" aria-labelledby="kd-zeiten-h">
              <h2 id="kd-zeiten-h">Zeiten</h2>
              <p>
                {res.zeiten.eintraege > 0 ? (
                  <>
                    Noch nicht berechnet: <b className="num">{stundenText(res.zeiten.offenMinuten)}</b> (
                    {eintraegeText(res.zeiten.eintraege)})
                  </>
                ) : (
                  'Keine offenen Zeiten.'
                )}
              </p>
              <div className="kd-zeiten-act">
                {rechnen && res.zeiten.eintraege > 0 && (
                  <Link to={`/rechnung/zeiten?abrechnen=${k.id}`} className="btn primary small">
                    Rechnung aus Zeiten
                  </Link>
                )}
                <Link to={`/rechnung/zeiten?kunde=${k.id}&monat=alle`} className="btn small">
                  Alle Zeiten
                </Link>
              </div>
            </div>
          )}
          <h2 id="kd-rechnungen-h">Rechnungen</h2>
          {res.rechnungen.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Noch keine Rechnung an diesen Kunden.</p>
              {rechnen && (
                <Link to={`/rechnung/neu?kunde=${k.id}`} className="btn primary small">
                  Erste Rechnung schreiben
                </Link>
              )}
            </div>
          ) : (
            <ul className="kd-rlist">
              {res.rechnungen.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => setView(r)} aria-label={`${docName(r)} ${r.number} ansehen`}>
                    <span className="kd-r-no num">{r.number}</span>
                    <span className="kd-r-date muted">
                      {fmtDate(r.invoiceDate)}
                      {r.kind === 'storno'
                        ? ` · Storno zu ${r.stornoOfNumber}`
                        : r.kind === 'korrektur'
                          ? ` · Korrektur zu ${r.stornoOfNumber}`
                          : r.art !== 'normal'
                            ? ` · ${ART_TITEL[r.art]}`
                            : ''}
                    </span>
                    <span className={`kd-r-amt num${r.amount < 0 ? ' neg' : ''}`}>{eurC(r.amount, r.waehrung)}</span>
                    <Status r={r} heute={res.heute} />
                    {r.kind === 'rechnung' && r.offen > 0 && r.offen !== r.amount && (
                      <span className="kd-r-rest">noch {eurC(r.offen, r.waehrung)} offen</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Kunden mit Rechnungen bleiben (ihre Nummer ist im DATEV-Export der Debitor) */}
      <p className="kd-remove">
        {res.rechnungen.length ? (
          <span className="muted">Kunden mit Rechnungen bleiben in der Liste, damit Ihre Buchhaltung stimmt.</span>
        ) : (
          pflegen && (
            <button type="button" className="linkbtn rt-danger" onClick={() => setWeg(true)}>
              Kunde entfernen
            </button>
          )
        )}
      </p>

      <PdfDialog
        url={view ? pdfUrl(view.id) : null}
        title={view ? `${docName(view)} ${view.number}` : ''}
        download={view ? `${pdfUrl(view.id)}?download=1` : undefined}
        onClose={() => setView(null)}
      />
      <ConfirmDialog
        open={weg}
        title={`Kunde „${k.name}“ entfernen?`}
        text={`Er verschwindet aus der Kundenliste. Rechnungen an ihn gibt es noch keine.${mitDazu ? ` ${mitDazu}` : ''}`}
        confirmLabel="Ja, entfernen"
        danger
        busy={busy}
        onConfirm={() => void entfernen()}
        onCancel={() => setWeg(false)}
      />
    </>
  );
}

function Zeile({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div>
      <dt>{titel}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Status({ r, heute }: { r: KundeRechnung; heute: string }) {
  if (r.kind === 'storno') return <span className="badge b-none">Storno</span>;
  if (r.kind === 'korrektur') return <span className="badge b-none">Korrektur</span>;
  if (r.erstattung > 0) return <span className="badge b-warn">Erstattung offen</span>;
  if (r.status === 'storniert') return <span className="badge b-none">Storniert</span>;
  if (r.ausgebucht) return <span className="badge b-none">Ausgebucht</span>;
  if (r.status === 'bezahlt') return <span className="badge b-ok">Bezahlt</span>;
  if (r.mahnstufe > 0) return <span className="badge b-bad">{MAHN_TITLES[r.mahnstufe - 1]}</span>;
  if (r.dueDate < heute) return <span className="badge b-bad">Überfällig</span>;
  return <span className="badge b-warn">{r.status === 'teilweise' ? 'Teilweise bezahlt' : 'Offen'}</span>;
}
